/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backgrid from 'backgrid';
import Backbone from 'backbone';
import _ from 'lodash';
import { View } from 'backbone.marionette';
import moment from 'moment';
import Ladda from 'ladda';

import Session from '@/Session';
import { formatDisplayDate, formatDisplayDateTime } from '@/util/MomentUtils';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';

import PageableCollection from '../../backbone/PageableCollection';
import DropzoneTextInputView from '../DropzoneTextInputView';

import NoteEntryCollectionView from './NoteEntryCollectionView';
import './NotesView.css';
import tmplNotesView from './NotesView.hbs';

const NotesView = View.extend({
  template: tmplNotesView,

  className: 'notes-view',

  ui: {
    addNote: '.add-note',
    noteEntryCollectionContainer: '.note-entry-collection-container',
    dropzoneTextInput: '.dropzone-text-input',
    awPackageTabView: '#awPackage',
    awPackageCollectionView: '#awPackageCollection',
    orderPackageCollection: '#orderPackageCollection',
    orderCollectionView: '#orderCollection',
    customerCollectionView: '#customerCollection',
    filterOrderPackageNotes: '[name="orderPackageNotes"]',
    filterOrderNotes: '[name="orderNotes"]',
    orderPackagePaginator: '.order-package-paginator-container',
    awPackagePaginator: '.aw-package-paginator-container',
    orderPaginator: '.order-paginator-container',
    customerPaginator: '.customer-paginator-container'
  },

  regions: {
    noteEntryCollectionContainer: '@ui.noteEntryCollectionContainer',
    dropzoneTextInput: '@ui.dropzoneTextInput',
    awPackageNotes: '#nav-aw-package',
    orderPackageCollectionView: '@ui.orderPackageCollection',
    awPackageCollectionView: '@ui.awPackageCollectionView',
    orderCollectionView: '@ui.orderCollectionView',
    customerCollectionView: '@ui.customerCollectionView',
    orderPackagePaginator: '@ui.orderPackagePaginator',
    awPackagePaginator: '@ui.awPackagePaginator',
    orderPaginator: '@ui.orderPaginator',
    customerPaginator: '@ui.customerPaginator'
  },

  events: {
    'click @ui.addNote': 'onClickAddNote',
    'click [data-bs-target]': 'renderTab',
    'change @ui.filterOrderPackageNotes': 'filterOrderPackageNotes',
    'change @ui.filterOrderNotes': 'filterOrderNotes'
  },

  childViewEvents: {
    'upload:documentUpload': 'onChildDropzoneDocumentUpload',
    'error:documentUploadError': 'onChildDropzoneDocumentUploadError'
  },

  filterOrderPackageNotes(e) {
    const filterValue = this.ui.filterOrderPackageNotes.val();
    let orderPackageNotes = this.model.get('orderPackageNotes') || [];

    if (filterValue === 'Show All') {
      this.orderPackageNoteCollection.fullCollection.reset(orderPackageNotes);
    } else {
      orderPackageNotes = _.filter(orderPackageNotes, { objectId: filterValue });
      this.orderPackageNoteCollection.fullCollection.reset(orderPackageNotes);
    }
  },

  filterOrderNotes(e) {
    const filterValue = this.ui.filterOrderNotes.val();
    let orderNotes = this.model.get('orderNotes') || [];

    if (filterValue === 'Show All') {
      this.orderNoteCollection.fullCollection.reset(orderNotes);
    } else {
      orderNotes = _.filter(orderNotes, { objectId: filterValue });
      this.orderNoteCollection.fullCollection.reset(orderNotes);
    }
  },

  //render each tab seprately when clicked, will re rerender everytime
  // renderTab(e, tab) {
  //  const tabName = e ? $(e.target).data('bsTarget') : tab;
  //   const view = new lazyLoadView(() => TestLoader(tabName));
  //   // this.showChildView(
  //   //   'content',
  //   //   lazyLoadView(() => TestLoader(4))
  //   // );
  //   this.showChildView('awPackage', view);
  // },

  initialize(options) {
    this.submitNoteService = options.submitNoteService;
  },

  onRender() {
    this.isAWPackage = this.model.get('isAWPackage');
    this.isCustomer = this.model.get('isCustomer');
    this.isOrderPackage = this.model.get('isOrderPackage');
    const awNotes = this.model.get('awNotes') || [];
    const orderPackageNotes = this.model.get('orderPackageNotes') || [];
    const orderNotes = this.model.get('orderNotes') || [];
    const customerNotes = this.model.get('customerNotes') || [];

    if (this.isAWPackage) {
      this.awNoteCollection = new PageableCollection(awNotes, {
        pageSize: 10
      });
      this.awPackageNoteEntryCollection = new NoteEntryCollectionView({
        collection: this.awNoteCollection
      });

      this.showChildView(
        'awPackagePaginator',
        new Backgrid.Extension.Paginator({
          collection: this.awNoteCollection
        })
      );
      this.showChildView('awPackageCollectionView', this.awPackageNoteEntryCollection);
    }

    //render OrderPackageNotes and OrderNotes
    if (this.isOrderPackage || this.isAWPackage) {
      this.orderPackageNoteCollection = new PageableCollection(orderPackageNotes, {
        pageSize: 10
      });
      this.orderPackageNoteEntryCollection = new NoteEntryCollectionView({
        collection: this.orderPackageNoteCollection
      });

      this.showChildView(
        'orderPackagePaginator',
        new Backgrid.Extension.Paginator({
          collection: this.orderPackageNoteCollection
        })
      );
      this.showChildView('orderPackageCollectionView', this.orderPackageNoteEntryCollection);

      this.orderNoteCollection = new PageableCollection(orderNotes, {
        pageSize: 10
      });
      this.orderNoteEntryCollection = new NoteEntryCollectionView({
        collection: this.orderNoteCollection
      });

      this.showChildView(
        'orderPaginator',
        new Backgrid.Extension.Paginator({
          collection: this.orderNoteCollection
        })
      );
      this.showChildView('orderCollectionView', this.orderNoteEntryCollection);
    }

    // render Customer Notes
    if (this.isCustomer) {
      this.customerNoteCollection = new PageableCollection(customerNotes, {
        pageSize: 10
      });
      this.customerNoteEntryCollection = new NoteEntryCollectionView({
        collection: this.customerNoteCollection
      });

      this.showChildView(
        'customerPaginator',
        new Backgrid.Extension.Paginator({
          collection: this.customerNoteCollection
        })
      );
      this.showChildView('customerCollectionView', this.customerNoteEntryCollection);
    }

    if (this.model.get('editable')) {
      this.dropzoneTextInput = new DropzoneTextInputView({
        model: new Backbone.Model({
          metadata: this.model.get('metadata'),
          tmplMethodXML: this.model.get('submitNoteAttachmentService'),
          pathToFileId: this.model.get('pathToFileId')
        })
      });
      this.showChildView('dropzoneTextInput', this.dropzoneTextInput);
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  async onSave(e, skipSave) {
    e?.preventDefault();
    if (!skipSave) {
      return await this.onClickAddNote(e);
    }
  },

  async onClickAddNote(e) {
    e?.preventDefault();

    let spinner;
    if (e) {
      spinner = Ladda.create(e.currentTarget);
      spinner.start();
    }

    const dropzoneData = this.dropzoneTextInput.getFormData();

    let formData = {
      memo: dropzoneData.dropzoneText,
      objectId: this.model.get('objectId')
    };

    try {
      let objectTypePlainText;
      await this.dropzoneTextInput.validate();
      this.toggleForm(false);
      await this.submitNoteService(formData);
      formData = { ...formData, ...this.getFormData() };
      formData.objectTypeId = this.model.get('objectTypeId');
      if (this.isAWPackage) {
        this.awNoteCollection.fullCollection.add(formData);
        objectTypePlainText = 'AW Package';
      } else if (this.isOrderPackage) {
        this.orderPackageNoteCollection.fullCollection.add(formData);
        objectTypePlainText = 'Order Package';
      } else if (this.isCustomer) {
        this.customerNoteCollection.fullCollection.add(formData);
        objectTypePlainText = 'Customer Dashboard';
      }

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Submit Success',
        message: `Note added to ${objectTypePlainText}.`,
        hideAfter: 5
      });

      this.toggleForm(true);

      this.dropzoneTextInput.resetDropzone();
    } catch (err) {
      this.showSaveErrorMessage();
    } finally {
      this.toggleForm(true);
      spinner.stop();
    }
  },

  getFormData() {
    const datetime = formatDisplayDateTime(moment());

    return {
      firstName: Session.user.firstName,
      lastName: Session.user.lastName,
      createdDate: formatDisplayDate(moment()),
      createdOn: datetime,
      createdTime: datetime.substr(datetime.indexOf(' ') + 1)
    };
  },

  onChildDropzoneDocumentUpload(formData) {
    formData = { ...formData, ...this.getFormData() };

    if (this.isAWPackage) {
      this.awNoteCollection.fullCollection.add(formData);
    } else if (this.isOrderPackage) {
      this.orderPackageNoteCollection.fullCollection.add(formData);
    } else if (this.isCustomer) {
      this.customerNoteCollection.fullCollection.add(formData);
    }

    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Submit Success',
      message: 'Attachment Added.'
    });

    this.dropzoneTextInput.resetDropzone();
  },

  onChildDropzoneDocumentUploadError(e) {
    if (e?.responseText.includes('already exists')) {
      showErrorMessage({
        errorCode: 'documentExists',
        errorTitle: 'Duplicate File Name',
        errorMessage:
          'All uploaded documents must have a unique file name. Please ensure that a document with the same name has not been uploaded in other areas of the form.'
      });
    } else {
      showErrorMessage({
        errorCode: 'uploadError',
        errorTitle: 'Upload Error',
        errorMessage: 'An error occurred while uploading this file. Please try again or contact an administrator.'
      });
    }
  },

  showSaveErrorMessage() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage: 'An error occurred while saving note. Please try again or contact an administrator.'
    });
  }
});

export default NotesView;
